import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { PublicPageShell } from './components/PublicPageShell'
import { fetchPublishedNews, type NewsEntry } from '@/features/news/news'
import { youtubeThumbnail } from '@/shared/utils/youtube'

/** Página pública "Lo último": listado completo (desde la BD) con filtro por categoría. */
export function NewsListPage() {
  const [news, setNews] = useState<NewsEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    let active = true
    fetchPublishedNews()
      .then((d) => active && setNews(d))
      .catch(() => {})
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [])

  const categories = useMemo(() => {
    const set = new Set<string>()
    news.forEach((n) => n.categories.forEach((c) => set.add(c)))
    return Array.from(set)
  }, [news])

  const list = filter === 'all' ? news : news.filter((n) => n.categories.includes(filter))

  return (
    <PublicPageShell>
      <div className="mb-8">
        <span className="text-xs font-bold uppercase tracking-widest text-primary">Lo último</span>
        <h1 className="mt-2 text-3xl lg:text-4xl font-bold tracking-tight">Novedades y casos de éxito</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="mr-2 size-5 animate-spin" /> Cargando…
        </div>
      ) : (
        <>
          {categories.length > 0 && (
            <div className="mb-8 flex flex-wrap gap-2">
              <FilterChip label="Todas" active={filter === 'all'} onClick={() => setFilter('all')} />
              {categories.map((c) => (
                <FilterChip key={c} label={c} active={filter === c} onClick={() => setFilter(c)} />
              ))}
            </div>
          )}

          {list.length === 0 ? (
            <p className="text-muted-foreground">No hay entradas publicadas todavía.</p>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((p) => {
                const cover = p.image || youtubeThumbnail(p.youtubeUrl)
                const tag = p.categories[0]
                return (
                  <Link
                    key={p.id}
                    to={`/lo-ultimo/${p.id}`}
                    className="group block overflow-hidden rounded-3xl border border-black/5 bg-white transition-all hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="aspect-[16/10] relative bg-gradient-to-br from-neutral-100 via-neutral-50 to-primary/5">
                      {cover ? (
                        <img src={cover} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="absolute inset-0 grid place-items-center text-3xl font-bold text-neutral-300">{(tag ?? p.title)[0]}</div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        {tag ? (
                          <span className="rounded-full bg-primary/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">{tag}</span>
                        ) : <span />}
                        <span className="text-xs text-muted-foreground">{p.date}</span>
                      </div>
                      <h3 className="text-base font-bold leading-snug group-hover:text-primary">{p.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </>
      )}
    </PublicPageShell>
  )
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
        active ? 'bg-foreground text-background' : 'bg-neutral-100 text-foreground hover:bg-neutral-200'
      }`}
    >
      {label}
    </button>
  )
}
